import { ClientContact } from "@jugl-web/rest-api";
import {
  Avatar,
  ListBoxItem,
  ListBoxItemComponent,
  ListBoxItemComponentProps,
  ListBoxProps,
} from "@jugl-web/ui-kit";
import { assert } from "@jugl-web/utils";
import { FC, useCallback, useMemo, useState } from "react";
import { useContactsList } from "../useContactsList";
import { ReactComponent as NewContactIcon } from "./assets/new-contact.svg";

type SupportedOverrides = Partial<
  Omit<ListBoxProps<ClientContact>, "onSelect">
>;

interface UseContactListBoxOptions {
  entityId: string;
  skipLoading?: boolean;
  onNewContactSelect?: () => void;
}

export const useContactListBox = ({
  entityId,
  skipLoading = false,
  onNewContactSelect,
}: UseContactListBoxOptions) => {
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: contacts,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    reset,
  } = useContactsList({
    entityId,
    searchTerm: searchQuery,
    skip: skipLoading,
  });

  const listItems = useMemo<ListBoxItem<ClientContact>[]>(
    () =>
      contacts.map((contact) => ({
        id: contact.id,
        value: contact,
      })),
    [contacts]
  );

  const attemptToLoadMoreContacts = useCallback(() => {
    if (isLoading || isLoadingMore || !hasMore) {
      return;
    }

    loadMore();
  }, [hasMore, isLoading, isLoadingMore, loadMore]);

  const getListBoxProps = useCallback(
    (overrides?: SupportedOverrides) =>
      ({
        items: listItems,
        selectionBehavior: { mode: "single", canToggle: false },
        hasSearch: true,
        shouldFilterOnSearch: false,
        loading: isLoading || isLoadingMore ? "bottom-spinner" : undefined,
        maxVisibleItems: 6,
        itemSize: "lg",
        spaceBetweenItems: "normal",
        renderCustomItem: (
          item: ListBoxItem<ClientContact>,
          _,
          props: ListBoxItemComponentProps
        ) => {
          assert(!!item.value, "Item value cannot be null at this point");

          return (
            <ContactListBoxItemComponent {...props} contact={item.value} />
          );
        },
        addButton: onNewContactSelect
          ? {
              label: "Add new customer",
              startIcon: <NewContactIcon />,
              onClick: onNewContactSelect,
            }
          : undefined,
        onReachEnd: attemptToLoadMoreContacts,
        onSearch: setSearchQuery,
        ...overrides,
      }) satisfies ListBoxProps<ClientContact>,
    [
      attemptToLoadMoreContacts,
      isLoading,
      isLoadingMore,
      listItems,
      onNewContactSelect,
    ]
  );

  return {
    getListBoxProps,
    clearSearchQuery: () => setSearchQuery(""),
    refresh: reset,
  };
};

interface ContactListBoxItemComponentProps extends ListBoxItemComponentProps {
  contact: ClientContact;
}

const ContactListBoxItemComponent: FC<ContactListBoxItemComponentProps> = ({
  contact,
  ...props
}) => (
  <ListBoxItemComponent
    {...props}
    buttonType="radio"
    label={contact.name ?? "Unknown"}
    startSlot={
      <Avatar
        className="border border-solid border-[#F2F2F2]"
        size="sm"
        username={contact.name ?? "Unknown"}
      />
    }
  />
);
